<template>
  <div
    class="v-code d-flex flex-column justify-content-center align-items-center"
  >
    <b-overlay
      ref="verifypOverlay"
      :show="loading"
      variant="white"
      opacity="0.7"
    >
      <div class="mb-4 mt-4 text-center">{{ $t("verification.label") }}</div>
      <div class="mb-5">
        <div class="v-digits mb-3 text-center">
          <template v-for="(n, index) in numbers">
            <input
              type="text"
              pattern="[0-9]"
              class="v-code-digit form-control"
              :autoFocus="
                autoFocus && !loading && !transition && index === autoFocusIndex
              "
              :key="`${id}-${index}`"
              :data-id="index"
              :value="n"
              :ref="fieldRefs[index]"
              v-on:input="onChanged"
              v-on:focus="onFocus"
              v-on:keydown="onKeyDown"
              v-on:paste="onPaste"
              :disabled="loading || transition"
              :required="required"
              maxlength="1"
            />
          </template>
        </div>
        <div
          :class="{
            fadeOut: !isMessageVisible,
            fadeIn: isMessageVisible,
            'text-danger': isError,
            'text-success': !isError,
          }"
          class="v-error"
        >
          {{ message }}
        </div>
      </div>

      <div class="text-secondary text-center mb-3">
        {{ $t("verification.question") }}
      </div>
    </b-overlay>
    <div class="mb-3">
      <b-button
        :disabled="!canResend || loading"
        variant="primary"
        @click="onResendClick"
      >
        {{ $t("verification.resendBtn") }}
      </b-button>
    </div>
    <div class="text-secondary text-center" :class="{ transparent: canResend }">
      {{ $t("verification.nextIn", [remaining]) }}
    </div>
  </div>
</template>

<script>
import * as errs from "@/util/errors";
import { SETUP_FROM_DATA } from "@/store/consts";
import {
  RESEND_EMAIL_INTERVAL_SECS,
  LONG_DESIGN_DELAY,
  URL_CONFIRM_USER_CODE,
  URL_RESEND_CONFIRMATION
} from "@/globals";

const KEY_CODE = {
  backspace: 8,
  left: 37,
  up: 38,
  right: 39,
  down: 40,
};

export default {
  name: "verification-code",
  props: {
    email: {
      type: Object,
      default: null,
    },
    size: {
      type: Number,
      default: 4,
    },
    required: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    // numbers - отдельные цифры
    // fieldRefs - рефы для полей ввода
    const { size, numbers } = this;
    let n;
    let autoFocusIndex = 0;
    if (numbers && numbers.length) {
      n = [];
      for (let i = 0; i < size; i++) {
        n.push(numbers[i] || "");
      }
      autoFocusIndex = 0; //numbers.length >= size ? 0 : numbers.length;
    } else {
      n = Array(size).fill("");
    }
    this.fieldRefs = [];
    for (let i = 0; i < size; i++) {
      this.fieldRefs.push(`vc_input_${i}`);
    }
    this.id = +new Date();
    return {
      numbers: n,
      autoFocusIndex,
      autoFocus: true,
      counter: 0,
      loading: false,
      cleaning: false,
      transition: false, // Задержка обновления компонента при ошибке
      message: "",
      error: true,
      messageVisible: false,
    };
  },
  computed: {
    isMessageVisible: function () {
      return this.messageVisible;
    },
    isError: function () {
      return this.error;
    },
    remaining: function () {
      return Math.floor(RESEND_EMAIL_INTERVAL_SECS - this.counter);
    },
    canResend: function () {
      return this.counter >= RESEND_EMAIL_INTERVAL_SECS;
    },
  },
  methods: {
    showError(msg) {
      this.error = true;
      this.message = msg;
      this.messageVisible = true;
    },
    showMessage(msg) {
      this.error = false;
      this.message = msg;
      this.messageVisible = true;
    },
    hideMessage() {
      this.message = "";
      this.messageVisible = false;
    },
    verify() {
      this.blur();
      this.hideMessage();
      this.$emit("verification", this.value);
      this.$post(
        URL_CONFIRM_USER_CODE.format(this.email.id, this.value),
        {},
        true,
        true,
        LONG_DESIGN_DELAY
      )
        .then((result) => {
          // Обновим либо инициализируем экземпляр пользователя
          this.$store.dispatch(SETUP_FROM_DATA, result.user);
          this.$emit("verifed", true);
          this.hideMessage();
        })
        .catch((error) => {
          // Добавим задержку для обновления компонента при ошибке.
          // Иначе можно очень быстро ввести код еще раз и вообще, не красиво.
          this.transition = true;
          this.delayedClear();

          if (error.fatal) {
            this.showError(this.$t("verification.serviceDown"));
            this.$alert.show(error);
          } else {
            console.log("Code: ", error);
            switch (error.code) {
              case errs.CODE_ACC_INVALID_CODE:
                this.showError(this.$t("verification.wrongCode"));
                break;
              case errs.CODE_ACC_ATTEMPTS_EXCEEDED:
                this.showError(this.$t("verification.attemptsExceeded"));
                break;
              default:
                this.showError(error.message);
                break;
            }
          }
          this.$emit("verifed", false);
        });
    },
    onResendClick() {
      this.blur();
      this.clear(true);
      this.loading = true;
      this.$post(URL_RESEND_CONFIRMATION.format(this.email.id), {}, true)
        .then(() => {
          this.showMessage(this.$t("verification.resendOk"));
          this.resetResendTimeout(0);
        })
        .catch(() => {
          this.$alert.show(this.$t("verification.resendFailed"));
        });
    },
    filter(value) {
      return value.replace(/[^\d]/gi, "");
    },
    // Заносит значение в массив и инициирует изменения
    // index - номер поля ввода
    // value - значение некоторой длины
    setValue(index, value) {
      let next;
      let { numbers, size } = this;
      if (!this.cleaning) this.hideMessage();
      numbers = Object.assign([], numbers);
      if (value.length > 1) {
        let nextIndex = value.length + index - 1;
        if (nextIndex >= size) {
          nextIndex = size - 1;
        }
        next = this.fieldRefs[nextIndex];
        const split = value.split("");
        split.forEach((item, i) => {
          const cursor = index + i;
          if (cursor < size) {
            numbers[cursor] = this.filter(item);
          }
        });
        this.numbers = numbers;
      } else {
        next = this.fieldRefs[index + 1];
        numbers[index] = value;
        this.numbers = numbers;
      }
      if (next && !this.cleaning) {
        const element = this.$refs[next][0];
        element.focus();
        element.select();
      }
      this.triggerChanges(numbers);
    },
    focusByIdx(idx) {
      if (idx >= this.size) return;
      const c = this.fieldRefs[idx];
      const element = this.$refs[c][0];
      element.focus();
      element.select();
    },
    blur() {
      document.activeElement.blur();
    },
    onFocus(e) {
      e.target.select(e);
    },
    onChanged(e) {
      e.preventDefault();
      const index = parseInt(e.target.dataset.id);
      const value = e.target.value.trim();
      e.target.value = this.filter(e.target.value);

      if (value != e.target.value) {
        // is-invalid
        this.showError(this.$t("verification.wrongSymbol"));
        return;
      }

      this.setValue(index, value);
    },
    onKeyDown(e) {
      const index = parseInt(e.target.dataset.id);
      const prevIndex = index - 1;
      const nextIndex = index + 1;
      const prev = this.fieldRefs[prevIndex];
      const next = this.fieldRefs[nextIndex];
      switch (e.keyCode) {
        case KEY_CODE.backspace: {
          e.preventDefault();
          const n = [...this.numbers];
          if (this.numbers[index]) {
            n[index] = "";
            this.numbers = n;
            this.triggerChanges(this.numbers);
          } else if (prev) {
            n[prevIndex] = "";
            this.$refs[prev][0].focus();
            this.numbers = n;
            this.triggerChanges(this.numbers);
          }
          break;
        }
        case KEY_CODE.left:
          e.preventDefault();
          if (prev) {
            this.$fieldRefs[prev][0].focus();
          }
          break;
        case KEY_CODE.right:
          e.preventDefault();
          if (next) {
            this.$fieldRefs[next][0].focus();
          }
          break;
        case KEY_CODE.up:
        case KEY_CODE.down:
          e.preventDefault();
          break;
        default:
          break;
      }
    },
    onPaste(e) {
      e.preventDefault();
      const index = parseInt(e.target.dataset.id);

      let data = null;
      let clipboardData = e.clipboardData; // IE
      if (!clipboardData) {
        // Chrome
        clipboardData = e.originalEvent.clipboardData;
      }
      data = this.filter(clipboardData.getData("Text"));
      if (data) {
        this.setValue(index, data);
      }
    },
    clear(clearError = false) {
      this.cleaning = true;
      try {
        this.setValue(0, "    ");
        this.autoFocusIndex = 0;
        if (clearError) this.hideMessage();

        this.$nextTick(function () {
          this.focusByIdx(0);
        });
      } finally {
        this.cleaning = false;
      }
    },
    delayedClear() {
      setTimeout(() => {
        this.transition = false;
        this.clear();
      }, 1000);
    },
    triggerChanges(values = this.numbers) {
      const { size } = this;
      this.value = values.join("");
      this.$emit("change", this.value);
      if (this.value.length >= size) {
        this.$emit("complete", this.value);
        this.verify();
      }
    },
    updateCounter() {
      this.counter++;
      if (this.counter >= RESEND_EMAIL_INTERVAL_SECS) {
        clearInterval(this.timerId);
      }
    },
    resetResendTimeout(elapsed) {
      this.counter = elapsed;
      this.timerId = setInterval(() => {
        this.updateCounter();
      }, 1000);
    },
  },
  mounted() {
    this.resetResendTimeout(
      (Date.now() - new Date(this.email.token_generated_at)) / 1000
    );
  },
};
</script>

<style>
@import "../animation.scss";

.v-code {
  display: inline-block;
}
.v-code-digit {
  display: inline-block;
  padding: 0;
  width: 2em;
  height: 2em;
  line-height: 2em;
  font-size: 1.5em;
  text-align: center;
}
@media (max-width: 550px) {
  .v-code-digit {
    width: 1.8em;
    height: 1.8em;
    line-height: 1.8em;
    font-size: 1.3em;
  }
}
.v-code-digit:not(:last-child) {
  margin-right: 1em;
}
.v-error {
  text-align: center;
}
.v-error::after {
  content: ".";
  opacity: 0;
}
</style>

